/**
 * Generated tile for a missing (or TikTok-default) cover. Never a flat grey square: always a calm gradient (hashed on
 * the genre, or on the title when the genre is missing / TikTok's catch-all "ALL"), a music glyph and a label — the
 * prettified genre, else "Instrumental" for instrumental sounds, else the first word of the title.
 */
import { Music2 } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { genreTileColors } from './coverColors';
import { formatGenreLabel } from './trendInsights';

/** First word of a title without surrounding punctuation ("“Sunny days”" → "Sunny"). */
function firstWord(title: string | null | undefined): string | null {
  const w = (title ?? '').trim().split(/\s+/)[0] ?? '';
  const clean = w.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
  return clean || null;
}

/** The tile's label: prettified genre → "Instrumental" (when known) → first word of the title → "Instrumental". */
// eslint-disable-next-line react-refresh/only-export-components -- pure helper owned by the tile (tested directly)
export function coverTileLabel({ genre, title, instrumental }: { genre?: string | null; title?: string | null; instrumental?: boolean }): string {
  const g = formatGenreLabel(genre);
  if (g) return g;
  if (instrumental) return 'Instrumental';
  return firstWord(title) ?? 'Instrumental';
}

export function GenreTile({ genre, title, instrumental, label, size = 'tile', className }: {
  genre?: string | null;
  /** Colours and label fall back to the title when there is no usable genre. */
  title?: string | null;
  /** Known instrumental (studio sounds): labelled "Instrumental" when there is no genre. */
  instrumental?: boolean;
  /** Accessible name (e.g. "Cover art for <title>"); omit for a decorative tile. */
  label?: string;
  /** `tile` = large square with the label; `thumb` = small square, glyph only (label in the native tooltip). */
  size?: 'tile' | 'thumb';
  className?: string;
}) {
  const g = formatGenreLabel(genre);
  const c = genreTileColors(g ?? title);
  const name = coverTileLabel({ genre, title, instrumental });
  return (
    <div
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      data-placeholder-cover="true"
      data-cover-label={name}
      title={size === 'thumb' ? name : undefined}
      className={cn(
        'flex h-full w-full overflow-hidden',
        // Large tiles: glyph + label sit in the top-left like an album label, clear of the centred play button.
        size === 'tile' ? 'flex-col items-start justify-start gap-1 p-3' : 'items-center justify-center',
        className,
      )}
      style={{ backgroundImage: `linear-gradient(135deg, ${c.from}, ${c.to})`, color: c.ink }}
    >
      <Music2 className={size === 'tile' ? 'h-4 w-4 opacity-70' : 'h-4 w-4 opacity-70'} aria-hidden />
      {size === 'tile' && (
        <span className="max-w-[85%] truncate text-[13px] leading-tight" style={{ fontWeight: 600 }}>{name}</span>
      )}
    </div>
  );
}

/**
 * The 9:16 media fallback for a song without a TikTok video (trend cards and the stage): the cover at half width,
 * centred, over the same cover blurred — or the genre tile at the same size over its own gradient when there is no cover.
 */
export function CoverFallback({ coverUrl, genre, title }: { coverUrl: string | null; genre: string | null; title: string }) {
  const label = `Cover art for ${title}`;
  const c = genreTileColors(formatGenreLabel(genre) ?? title);
  return (
    <div data-media="cover" className="relative flex h-full w-full items-center justify-center overflow-hidden bg-[#f2f4f7]">
      {coverUrl ? (
        <img src={coverUrl} alt="" aria-hidden className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover opacity-60 blur-2xl" loading="lazy" />
      ) : (
        <div aria-hidden className="pointer-events-none absolute inset-0 scale-110 opacity-60 blur-2xl" style={{ backgroundImage: `linear-gradient(135deg, ${c.from}, ${c.to})` }} />
      )}
      <div className="relative aspect-square w-1/2 overflow-hidden rounded-[10px] shadow-[0_4px_16px_rgba(16,24,40,0.12)]">
        {coverUrl
          ? <img src={coverUrl} alt={label} className="h-full w-full object-cover" loading="lazy" />
          : <GenreTile genre={genre} title={title} label={label} />}
      </div>
    </div>
  );
}
